import styles from './SidebarNav.scss';

import cx from 'classnames';
import { Entry, ReferencedEntry } from 'contentful';
import debounce from 'lodash/debounce';
import React from 'react';

import { ContentType, HasImage, HasVideo, LinkSet, as } from 'src/api/contentful/contentTypes.cloverdotcom';
import { withSidebarContext } from 'src/components/SidebarContext/WithSidebarContext';
import { componentKey, getField, isEntry } from 'src/utils/EntryUtil';
import { ProcessedNavigation, VerticalLinkSet } from 'src/utils/NavigationUtil';

import { SidebarItem } from './SidebarItem/SidebarItem';

export const INITIAL_UI_STATE: Readonly<UiState> = {
  activeSubmenu: undefined,
  activeIndex: -1,
  history: [],
};

interface ISidebarNav {
  open?: boolean;
  entry?: Entry<ProcessedNavigation>;
  setBackgroundColor: (backgroundColor?: string) => void;
  setMediaOverride: (mediaOverride?: ReferencedEntry<HasVideo | HasImage>) => void;
  closeSidebar: () => void;
  children?: never;
}

type MenuHistoryRecord = Readonly<{
  activeIndex: number;
  submenu?: ReferencedEntry<LinkSet | VerticalLinkSet>;
}>;

interface UiState {
  activeIndex: number;
  history: ReadonlyArray<MenuHistoryRecord>;
  activeSubmenu?: ReferencedEntry<LinkSet & VerticalLinkSet>;
}

class SidebarNav extends React.PureComponent<ISidebarNav, UiState> {

  readonly state: UiState = { ...INITIAL_UI_STATE };

  readonly clearActiveIndex = debounce(() => this.setState({ activeIndex: -1 }));

  readonly button = React.createRef<HTMLButtonElement>();

  componentDidUpdate(prevProps: Readonly<ISidebarNav>, prevState: Readonly<UiState>) {
    // from active to inactive
    if (prevState.activeIndex > -1 && this.state.activeIndex === -1) {
      this.updateSidebar();

      if (this.button.current) this.button.current.focus();
    }

    // from open to closed
    if (prevProps.open && !this.props.open) {
      this.resetState();
    }
  }

  render() {
    const { activeIndex } = this.state;
    const {
      entry,
      open,
      setBackgroundColor,
      setMediaOverride,
      closeSidebar,
      ...props
    } = this.props;

    const submenu = this.state.activeSubmenu;

    const items = getField(submenu, 'items') || getField(entry, 'items') || [];

    const submenuTitle = getField(as<LinkSet>(ContentType.LINK_SET, submenu), 'title');

    return (
      <div
        className={styles.sidebarNav}
        onClick={this.onClick}
        {...props}
      >
        {submenu ? (
          <button
            className={cx(styles.title, styles.titleBackButton)}
            ref={this.button}
            onFocus={this.clearActiveIndex}
            onClick={this.closeSubmenu}
            aria-label="back to options"
          >
            {submenuTitle}
          </button>
        ) : (
          <h4 className={styles.title}>{getField(entry, 'title')}</h4>
        )}
        <ul
          className={styles.menu}
          onClick={this.onMenubarClick}
          onMouseLeave={this.clearActiveIndex}
        >
          {items.map((entry, index) => !isEntry(entry) ? null : (
            <li className={styles.item} key={componentKey(entry, index)}>
              <SidebarItem
                open={open}
                parentEntry={submenu}
                className={cx(styles.itemContent, {
                  [styles.itemContentInactive]: index !== activeIndex && activeIndex > -1,
                  [styles.itemContentActive]: index === activeIndex,
                })}
                entry={entry}
                active={index === activeIndex}
                onActivate={(backgroundColor, mediaOverride) => this.onActivate(index, backgroundColor, mediaOverride)}
                onOpen={this.openSubmenu}
                titleTag="h5"
              />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  readonly onClick: React.MouseEventHandler<HTMLElement> = e => {
    // stop bubble if it came from a button within the open navbar
    if (e.target instanceof HTMLButtonElement) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  updateSidebar(backgroundColor?: string, mediaOverride?: ReferencedEntry<HasVideo | HasImage>) {
    const { activeSubmenu } = this.state;

    const verticalMarket = getField(activeSubmenu, 'verticalMarket');

    this.props.setBackgroundColor(backgroundColor || getField(verticalMarket, 'primaryColor'));
    this.props.setMediaOverride(mediaOverride || verticalMarket);
  }

  readonly onActivate = (index: number, backgroundColor?: string, mediaOverride?: ReferencedEntry<HasVideo | HasImage>) => {
    const { activeIndex } = this.state;

    this.updateSidebar(backgroundColor, mediaOverride);

    if (activeIndex === index) return;

    this.setState({ activeIndex: index });
  };

  readonly openSubmenu = (submenu: ReferencedEntry<LinkSet | VerticalLinkSet>) => {
    const { activeIndex, history, activeSubmenu } = this.state;

    this.setState({
      activeSubmenu: submenu,
      activeIndex: -1,
      history: [
        ...history,
        {
          activeIndex,
          submenu: activeSubmenu,
        },
      ],
    });
  };

  readonly closeSubmenu = () => {
    const { history } = this.state;

    const last = history.length > 0 ? history[history.length - 1] : undefined;

    this.setState({
      activeSubmenu: last && last.submenu,
      activeIndex: last && typeof last.activeIndex === 'number' ? last.activeIndex : -1,
      history: history.length > 0 ? history.slice(0, history.length - 1) : [],
    });
  };

  readonly resetState = () => this.setState({ ...INITIAL_UI_STATE });

  readonly onMenubarClick: React.MouseEventHandler<HTMLUListElement> = e => {
    if (!(e.target instanceof HTMLAnchorElement)) return;
    this.resetState();
    this.props.closeSidebar();
  };
}

const SidebarContexted = withSidebarContext(({
  isOpen,
  setBackgroundColor,
  setMedia,
  close,
}) => ({
  open: isOpen,
  setBackgroundColor,
  setMediaOverride: setMedia,
  closeSidebar: close,
}))(SidebarNav);

export {
  SidebarContexted as SidebarNav,
  SidebarNav as _SidebarNav,
};
